import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { Menu } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ActionMenuItem from "@entrilia/shared/components/ActionMenuItem";
import ReportAuthorizedBox from "@entrilia/shared/components/ReportAuthorizedBox";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";

import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import type {
  ChangeReportGroupRequest,
  RenameReportRequest,
  Report,
} from "../../../../../../../../api/types/svTypes.ts";
import ChangeReportGroup from "../../../../../report-lists/reports/components/ChangeReportGroup.tsx";
import DeleteReport from "../../../../../report-lists/reports/components/DeleteReport.tsx";
import RenameReport from "../../../../../report-lists/reports/components/RenameReport.tsx";
import { useEnhancedBiApiClientProvider } from "../../../../contexts/ApiClientProviderContext";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch";
import {
  selectCurrentReportAuthorizationInfo,
  selectReportEditMode,
} from "../../../../store/selectors/currentReportSelectors";
import { selectVersionHistoryOpen } from "../../../../store/selectors/uiSettingsSelectors.ts";
import { updateReport } from "../../../../store/thunks/currentReportThunks";
import { createUiSettingsAction } from "../../../../store/utils/storeUtils.ts";

interface Props {
  anchorEl: HTMLElement | null;
  report: Report;
  readonlyMode?: boolean;
  onClose: () => void;
  onDeleted?: () => void;
}

export default function ReportActions({ anchorEl, report, onClose, onDeleted, readonlyMode }: Props) {
  const { renameReport, changeReportGroup, deleteReports } = useEnhancedBiApiClientProvider();
  const { sendNotification } = useNotificationContext();
  const dispatch = useAppDispatch();
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>();
  const [openRenameDialog, setOpenRenameDialog] = useState<boolean>();
  const [openChangeLevelDialog, setOpenChangeLevelDialog] = useState<boolean>();
  const [open, setOpen] = useState(() => !!anchorEl);
  const reportAuthorization = useSelector(selectCurrentReportAuthorizationInfo);
  const versionHistoryOpen = useSelector(selectVersionHistoryOpen);
  const isEditMode = useSelector(selectReportEditMode);

  const onDeleteReport = useCallback(() => {
    setOpen(false);
    setOpenDeleteDialog(true);
  }, [setOpenDeleteDialog]);

  const onRenameReport = useCallback(() => {
    setOpen(false);
    setOpenRenameDialog(true);
  }, [setOpenRenameDialog]);

  const onChangeLevelReport = useCallback(() => {
    setOpen(false);
    setOpenChangeLevelDialog(true);
  }, [setOpenChangeLevelDialog]);

  useEffect(() => {
    if (openDeleteDialog === false || openRenameDialog === false || openChangeLevelDialog === false) {
      onClose();
    }
  }, [openDeleteDialog, openRenameDialog, openChangeLevelDialog, onClose]);

  const handleRenameReport = useCallback(
    async (request: RenameReportRequest) => {
      const result = await renameReport(request);
      if (result.success && result.data) {
        return result.data;
      }
      throw new Error("Failed to rename report");
    },
    [renameReport]
  );

  const handleChangeReportGroup = useCallback(
    async (request: ChangeReportGroupRequest) => {
      const result = await changeReportGroup(request);
      if (result.success && result.data) {
        return result.data;
      }
      throw new Error("Failed to change report group");
    },
    [changeReportGroup]
  );

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        slotProps={{
          paper: { sx: { width: "200px" } },
        }}
      >
        {isEditMode && (
          <ReportAuthorizedBox
            permissions={["ManageReports"]}
            requiredAccess={ReportAccessType.Write}
            access={reportAuthorization?.access}
          >
            <ActionMenuItem
              key={"rename"}
              icon={<DriveFileRenameOutlineOutlinedIcon />}
              title="Rename"
              onClick={onRenameReport}
              disabled={readonlyMode}
            />
          </ReportAuthorizedBox>
        )}

        {isEditMode && (
          <ReportAuthorizedBox
            permissions={report.inheritFromParent ? ["ShareReports"] : ["ManageReports"]}
            requiredAccess={report.inheritFromParent ? ReportAccessType.FullAccess : ReportAccessType.Write}
            access={reportAuthorization?.access}
          >
            <ActionMenuItem
              key={"changeLevel"}
              icon={<DriveFileMoveOutlinedIcon />}
              title="Change Group"
              onClick={onChangeLevelReport}
              disabled={readonlyMode}
            />
          </ReportAuthorizedBox>
        )}

        {isEditMode && (
          <ReportAuthorizedBox
            permissions={["ManageReports"]}
            requiredAccess={ReportAccessType.Write}
            access={reportAuthorization?.access}
          >
            <ActionMenuItem
              key={"versionHistory"}
              icon={<HistoryOutlinedIcon />}
              title="Version history"
              onClick={() => {
                onClose();
                dispatch(createUiSettingsAction("setVersionHistoryOpen", true));
              }}
              disabled={versionHistoryOpen}
            />
          </ReportAuthorizedBox>
        )}
        <ReportAuthorizedBox
          permissions={["ManageReports"]}
          requiredAccess={ReportAccessType.Owner}
          access={reportAuthorization?.access}
        >
          <ActionMenuItem
            key={"delete"}
            icon={<DeleteOutlineOutlinedIcon color="error" />}
            title="Delete"
            onClick={onDeleteReport}
            disabled={readonlyMode}
          />
        </ReportAuthorizedBox>
      </Menu>
      {openDeleteDialog && report && (
        <DeleteReport
          reports={[report]}
          doDelete={deleteReports}
          onDeleted={() => {
            sendNotification("The report was successfully deleted.");
            onDeleted?.call(null);
          }}
          onClose={() => setOpenDeleteDialog(false)}
        />
      )}
      {openRenameDialog && report && (
        <RenameReport
          report={report}
          renameReport={handleRenameReport}
          onChanged={(r) => {
            dispatch(updateReport({ name: r.name }));
            sendNotification("The report was successfully renamed.");
          }}
          onClose={() => setOpenRenameDialog(false)}
        />
      )}
      {openChangeLevelDialog && report && (
        <ChangeReportGroup
          report={report}
          changeGroup={handleChangeReportGroup}
          onChanged={(r) => {
            dispatch(updateReport({ groupId: r.groupId }));
            sendNotification("The group was changed for the selected report.");
          }}
          onClose={() => setOpenChangeLevelDialog(false)}
        />
      )}
    </>
  );
}
